// =============================================================================
// TrackerTable.jsx
//
// Tabela de entradas do Tracker. Componente de apresentação puro:
//   → Recebe dados via props
//   → Não faz chamadas à API
//   → Só tem estado local de paginação
//
// Props:
//   entries    → array de entradas (do reducer)
//   loading    → boolean — mostra barra de progresso no topo
//   onRowClick → callback(row) — clicar numa linha ou no ícone Edit
//   onDelete   → callback(id)  — clicar no ícone Delete
// =============================================================================

import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, Chip, IconButton, Typography, Box, Tooltip,
  LinearProgress,
} from '@mui/material';
import EditIcon          from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Normaliza datas da API para yyyy-MM-dd para display na tabela.
// Cobre MM/dd/yyyy (en-US), yyyy-dd-MM, e ISO yyyy-MM-dd.
const fmtDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[1]}-${slash[2]}`;
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, yyyy, a, b] = dash;
    return Number(a) > 12 ? `${yyyy}-${b}-${a}` : `${yyyy}-${a}-${b}`;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString().slice(0, 10);
};

// ── Cores dos chips ───────────────────────────────────────────────────────────

// ── Cabeçalhos da tabela ──────────────────────────────────────────────────────

const HEADERS = ['Activity', 'Team', 'Tech', 'Vendor', 'Feature', 'Start', 'End', 'Owner', 'Phases', ''];

const headerCellSx = {
  fontWeight: 700, fontSize: '0.68rem',
  textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'text.disabled', whiteSpace: 'nowrap',
};

// ── Componente ────────────────────────────────────────────────────────────────

const TrackerTable = ({ entries, loading, onRowClick, onDelete }) => {
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const start     = page * rowsPerPage;
  const paginated = entries.slice(start, start + rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>

      {loading && <LinearProgress sx={{ height: 2 }} />}

      {!loading && entries.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activities yet. Click <strong>+ New Activity</strong> to start.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">

              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {HEADERS.map((h) => (
                    <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map((entry) => (
                  <TableRow
                    key={entry.id}
                    hover
                    onClick={() => onRowClick(entry)}
                    sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  >
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {entry.title}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {entry.teamName || '—'}
                    </TableCell>

                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {entry.technology || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {entry.vendor || '—'}
                    </TableCell>

                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {entry.feature || '—'}
                    </TableCell>

                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {fmtDate(entry.startDate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {fmtDate(entry.endDate)}
                    </TableCell>

                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {(entry.ownerNames?.length ? entry.ownerNames.join(', ') : entry.ownerName) || '—'}
                    </TableCell>

                    {/* Resumo visual das phases */}
                    <TableCell>
                      {(() => {
                        const phases = entry.phases || [];
                        const rollbackCount = phases.filter(ph => ph.label === 'Rollback').length;
                        const phaseCount = phases.length - rollbackCount;
                        return (
                          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                            {phaseCount > 0 && (
                              <Chip
                                label={`${phaseCount} Phase${phaseCount !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {rollbackCount > 0 && (
                              <Chip
                                label={`${rollbackCount} Rollback${rollbackCount !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {phases.length === 0 && (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>

                    {/* Botões — stopPropagation impede acionar o onRowClick da linha */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onRowClick(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => onDelete(entry.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                  </TableRow>
                ))}
              </TableBody>

            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={entries.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </>
      )}
    </Paper>
  );
};

export default TrackerTable;